import { SEMANTIC_CHAT_SEARCH_QUERY_KEY } from "@frontend-shared/constants";
import type { TCrossChatSearchResponse } from "@frontend-shared/types";
import { useQuery } from "@tanstack/react-query";

import { chatApi } from "@/api/services";

// NOTE: Minimum query length the hook will actually fire against the server.
// Mirrors the server-side `MinLength(2)` so we don't bother the API with a
// query that will only return a 400 anyway.
const MIN_QUERY_LENGTH = 2;

interface IUseSemanticChatSearchParams {
  query: string;
  // NOTE: NSAI-6589 — false where cross-chat search is unavailable (on-premise
  // without a search embedding model), so the palette never pays for a request
  // the deployment cannot answer.
  enabled?: boolean;
}

// NOTE: Cross-chat semantic search. Caller is expected to debounce `query`
// — the hook does not. The query only fires when `query.trim()` reaches
// the MIN_QUERY_LENGTH gate, mirroring the server-side `MinLength(2)`,
// so a single-character keystroke never burns an embedding round-trip.
// `staleTime` keeps the last response around briefly so a quick
// close/re-open of the dialog reuses it instead of re-firing.
export const useSemanticChatSearch = ({
  query,
  enabled = true,
}: IUseSemanticChatSearchParams) => {
  const trimmed = query.trim();
  const isLongEnough = trimmed.length >= MIN_QUERY_LENGTH;

  return useQuery<TCrossChatSearchResponse>({
    queryKey: [...SEMANTIC_CHAT_SEARCH_QUERY_KEY, trimmed],
    queryFn: () => chatApi.searchAcrossChats({ q: trimmed }),
    enabled: enabled && isLongEnough,
    staleTime: 30_000,
  });
};
